import type { ReactElement, ReactNode } from "react";
import { Menu, type LucideIconName } from "@/deps/ui-library";
import { cn } from "@/lib/cn";

/**
 * A top-bar dropdown (org switcher, nav sections, + New, user): RDS `Menu`, on Ark UI.
 *
 * Ark brings what the hand-built surface lacked — arrow keys between items, Home/End, typeahead,
 * focus moved into the menu and back to its trigger, Escape and outside click. The opener still
 * owns the open state, so the trigger can show it (its chevron, its wash) and only one menu is
 * open at a time. The trigger is the bar's own button, passed through as it is: the bar keeps its
 * own design (ADR-0054), only the panel is RDS's.
 */
export function BarMenu({
  open,
  onOpenChange,
  trigger,
  align = "left",
  width = "auto",
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A single button element; Ark wires the menu's aria and keys onto it. */
  trigger: ReactElement;
  align?: "left" | "right";
  width?: "auto" | "md" | "lg";
  children: ReactNode;
}) {
  return (
    <Menu.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
      positioning={{ placement: align === "right" ? "bottom-end" : "bottom-start", gutter: 6 }}
    >
      <Menu.Trigger asChild>{trigger}</Menu.Trigger>
      <Menu.Content width={width}>{children}</Menu.Content>
    </Menu.Root>
  );
}

/** A labelled run of items. Uppercase is the one place the design keeps it: overlay group labels. */
export function BarMenuGroup({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <Menu.Group>
      {label ? <Menu.Label className="px-2 pt-1.5 pb-0.5 font-medium">{label}</Menu.Label> : null}
      {children}
    </Menu.Group>
  );
}

export function BarMenuItem({
  value,
  onSelect,
  disabled,
  active,
  tone = "default",
  icon,
  meta,
  children,
}: {
  /** Unique within its menu. */
  value: string;
  onSelect: () => void;
  disabled?: boolean;
  /** The current page: shown, and said to a screen reader. */
  active?: boolean;
  tone?: "default" | "danger";
  icon?: LucideIconName;
  /** A count or a plan badge, in mono on the right. */
  meta?: string;
  children: ReactNode;
}) {
  return (
    <Menu.Item
      value={value}
      onClick={onSelect}
      disabled={disabled}
      icon={icon}
      shortcut={meta}
      variant={tone === "danger" ? "danger" : "default"}
      className={cn(
        "px-2 py-[7px] text-sm",
        // The current page keeps its wash under the pointer and the keyboard highlight too, so it
        // never looks like any other item. RDS's class merge lets these replace its resting ones.
        active &&
          "bg-accent text-primary hover:bg-accent data-[highlighted]:bg-accent border-primary rounded-l-none border-l-2 font-semibold [&_svg]:text-primary",
      )}
    >
      {children}
      {active ? <span className="sr-only"> (current page)</span> : null}
    </Menu.Item>
  );
}
